import { useEffect, useMemo, useState } from "react";
import { Check, MagnifyingGlass, X } from "@phosphor-icons/react";
import { API } from "@/lib/api";

const photoUrl = (p, w) => `${API}/public/showroom/photo/${p}?w=${w}`;

// Slabs on the racks (in stock + remnants, never made-to-order) that have a website
// photo — one entry per color — for the stone picker in the 3D edge viewer.
export const useEdgeStones = (enabled) => {
  const [stones, setStones] = useState(null);
  useEffect(() => {
    if (!enabled || stones) return;
    fetch(`${API}/public/showroom`)
      .then((r) => (r.ok ? r.json() : Promise.reject(r)))
      .then((d) => {
        const seen = new Set();
        const out = [];
        for (const i of d.items || []) {
          if (i.special_order || !i.photo || seen.has(i.color)) continue;
          seen.add(i.color);
          out.push({
            key: i.key,
            name: i.color,
            brand: i.brand || "",
            material: i.material || "",
            finish: i.finish || "",
            kind: i.kind,
            thumb: photoUrl(i.thumb || i.photo, 240),
            photo: photoUrl(i.edge_photo || i.photo, 1600),
          });
        }
        out.sort((a, b) => a.name.localeCompare(b.name));
        setStones(out);
      })
      .catch(() => setStones([]));
  }, [enabled, stones]);
  return stones;
};

const Row = ({ s, selected, onPick, testId }) => (
  <button
    type="button"
    data-testid={testId}
    aria-pressed={selected}
    onClick={() => onPick(s)}
    className={`flex w-full items-center gap-3 border-b border-[#eeebe4] px-4 py-3 text-left transition-colors duration-200 hover:bg-[#f3f1ec] ${
      selected ? "bg-[#f3f1ec]" : ""
    }`}
  >
    {s.thumb ? (
      <img src={s.thumb} alt="" className="h-14 w-14 shrink-0 object-cover" loading="lazy" />
    ) : (
      <span className="h-14 w-14 shrink-0 bg-[linear-gradient(135deg,#efe7d6,#d9cdb5)]" />
    )}
    <span className="min-w-0 flex-1">
      <span className="block truncate text-sm font-semibold text-[#1c1b19]">{s.name}</span>
      <span className="block truncate text-xs text-[color:var(--s-mute)]">
        {[s.brand, s.material, s.finish].filter(Boolean).join(" · ") || s.meta}
        {s.kind === "Remnant" && " · Remnant"}
      </span>
    </span>
    {selected && <Check size={16} weight="bold" className="shrink-0 text-[color:var(--s-gold)]" />}
  </button>
);

// Slide-in drawer over the 3D canvas: search, material chips, the default stone, then
// every color on the racks.
export const StonePicker = ({ open, onClose, stones, current, fallback, onPick }) => {
  const [q, setQ] = useState("");
  const [mat, setMat] = useState("all");
  const materials = useMemo(
    () => [...new Set((stones || []).map((s) => s.material).filter(Boolean))].sort(),
    [stones],
  );
  const list = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (stones || []).filter(
      (s) =>
        (mat === "all" || s.material === mat) &&
        (!needle || `${s.name} ${s.brand} ${s.material}`.toLowerCase().includes(needle)),
    );
  }, [stones, q, mat]);
  const showDefault =
    mat === "all" && (!q.trim() || fallback.name.toLowerCase().includes(q.trim().toLowerCase()));

  return (
    <aside
      className={`absolute inset-y-0 right-0 z-10 flex w-full max-w-[380px] flex-col bg-white shadow-[-30px_0_60px_-30px_rgba(0,0,0,0.35)] transition-transform duration-500 ease-[cubic-bezier(.2,.7,.2,1)] ${
        open ? "translate-x-0" : "translate-x-full"
      }`}
      data-testid="edge-stone-picker"
      aria-hidden={!open}
    >
      <div className="flex items-center justify-between gap-3 border-b border-[#eeebe4] px-4 py-3">
        <span className="text-sm font-semibold text-[#1c1b19]">Pick a stone</span>
        <button
          type="button"
          data-testid="edge-stone-picker-close"
          onClick={onClose}
          aria-label="Close stone picker"
          className="flex h-9 w-9 items-center justify-center text-[#1c1b19]/70 transition-colors duration-200 hover:bg-[#f3f1ec] hover:text-[#1c1b19]"
        >
          <X size={16} weight="bold" />
        </button>
      </div>
      <div className="border-b border-[#eeebe4] px-4 py-3">
        <label className="flex h-10 items-center gap-2 border border-[#e2ded5] px-3">
          <MagnifyingGlass size={14} className="text-[color:var(--s-mute)]" />
          <input
            data-testid="edge-stone-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search the racks"
            className="h-full w-full bg-transparent text-sm text-[#1c1b19] outline-none placeholder:text-[color:var(--s-mute)]"
          />
        </label>
        {materials.length > 1 && (
          <div className="mt-3 flex flex-wrap gap-1.5" data-testid="edge-stone-materials">
            {["all", ...materials].map((m) => (
              <button
                key={m}
                type="button"
                data-testid={`edge-stone-mat-${m.toLowerCase().replace(/\s+/g, "-")}`}
                aria-pressed={mat === m}
                onClick={() => setMat(m)}
                className={`h-8 px-3 text-xs font-semibold transition-colors duration-200 ${
                  mat === m ? "bg-[#1c1b19] text-white" : "bg-[#f3f1ec] text-[#1c1b19] hover:bg-[#e8e4db]"
                }`}
              >
                {m === "all" ? "All" : m}
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto" data-testid="edge-stone-list">
        {showDefault && (
          <Row
            s={{
              ...fallback,
              meta: fallback.meta || (fallback.photo ? "Our default sample" : "Rendered look"),
            }}
            selected={current.key === fallback.key}
            onPick={() => onPick(null)}
            testId="edge-stone-option-default"
          />
        )}
        {stones === null && (
          <p className="px-4 py-6 text-sm text-[color:var(--s-mute)]" data-testid="edge-stone-loading">
            Loading the racks…
          </p>
        )}
        {list
          .filter((s) => s.key !== fallback.key)
          .map((s) => (
          <Row
            key={s.key}
            s={s}
            selected={current.key === s.key}
            onPick={onPick}
            testId={`edge-stone-option-${s.key}`}
          />
          ))}
        {stones && !list.length && (
          <p className="px-4 py-6 text-sm text-[color:var(--s-mute)]" data-testid="edge-stone-empty">
            Nothing on the racks matches that.
          </p>
        )}
      </div>
    </aside>
  );
};
